import { formatDate } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, forkJoin, of, take } from 'rxjs';
import { DocksModel } from 'src/app/models/DocksModel';
import { MotoresTransmissaoTripsModel } from 'src/app/models/MotoresTransmissaoResponse';
import { MotoresTransmissaoTimeTableModel } from 'src/app/models/MotoresTransmissaoTimeTable';
import { MotoresTransmissaoService } from 'src/app/services/motores-transmissao/motores-transmissao.service';
import { DateUtilsService } from 'src/app/shared/utils/date-utils.service';

@Component({
  selector: 'app-trip-detais-motores-transmissao',
  templateUrl: './trip-detais-motores-transmissao.component.html',
  styleUrls: ['./trip-detais-motores-transmissao.component.css']
})
export class TripDetailsMotoresTransmissaoComponent implements OnInit, OnDestroy {
  @Input() isViewOnly = false

  dateInterval: any
  updateInterval: any

  tripDBData?: MotoresTransmissaoTripsModel | undefined;
  motoresTransmissaoDocks: DocksModel[] = [];
  dockId!: number;
  dockData?: DocksModel | any = undefined;
  activeDock: boolean = false;

  isExtraTrip: boolean = false;
  isLoading: boolean = false;
  pausePooling: boolean = false;
  showIntegrationForm: boolean = false;

  bgColor: string = '#FFF';

  takt: string = "";
  definedArrival?: string | null;
  definedDeparture?: string | null;
  remainingRealTimeFormatted?: string | null;
  processDescription: string | null = null;
  tripTaktTime?: number;

  currentTimeInMs = Date.now();

  constructor(
    private readonly datesService: DateUtilsService,
    private readonly motoresTransmissaoService: MotoresTransmissaoService,
    private readonly route: ActivatedRoute,
    private readonly router: Router) { }

  ngOnInit(): void {

    this.route.paramMap.subscribe(params => {
      const newDockId = Number(params.get('id'))

      if (this.dockId !== newDockId) {
        this.dockId = newDockId
        this.loadDockData(true)
      }
      this.isViewOnly = this.router.url.includes("/view")
    })

    this.updateInterval = setInterval(() => {
      if (!this.pausePooling) {
        this.tripDBData?.trip ? this.refreshTripTimer() : this.defaultTimer();
      }
    }, 1000)
    this.dateInterval = setInterval(() => {
      if (!this.pausePooling) {
        this.updateData();
      }
    }, 5000);
  }

  private loadDockData(showLoading: boolean = false) {
    if (showLoading) {
      this.isLoading = true
    }

    this.pausePooling = true;

    this.clearDisplayedData()

    this.loadCurrentDockData().subscribe(({ docks, trips }) => {
      this.setCurrentDockData(docks, trips)
      this.pausePooling = false;
      this.isLoading = false;
      this.updateDisplayedData()
    })
  }

  private updateData(): void {
    this.pausePooling = true;

    this.loadCurrentDockData().subscribe(({ docks, trips }) => {
      this.setCurrentDockData(docks, trips)
      this.pausePooling = false;
      this.updateDisplayedData()
    })
  }

  private loadCurrentDockData() {
    return forkJoin({
      docks: this.motoresTransmissaoService.getEngineAndTransmissionDocksDataView().pipe(
        catchError(() => of([] as DocksModel[]))
      ),
      trips: this.motoresTransmissaoService.getActiveTripsView().pipe(
        catchError(() => of([] as MotoresTransmissaoTripsModel[]))
      )
    }).pipe(take(1))
  }

  private setCurrentDockData(docks: DocksModel[], trips: MotoresTransmissaoTripsModel[]): void {
    this.motoresTransmissaoDocks = docks.map(dock => ({
      ...dock,
      status: new Date(dock.status + 'Z')
    }))
    this.dockData = this.motoresTransmissaoDocks.find(dock => dock.id === this.dockId)
    this.tripDBData = trips.find(trip => trip.dockId === this.dockId)
  }

  private updateDisplayedData(): void {
    if (!this.shouldUpdateDockInfo()) {
      this.resetTimer()
      return
    }

    this.activeDock = true
    if (this.tripDBData?.trip) {
      this.checkData()
    } else {
      this.definedArrival = null
      this.definedDeparture = null
      this.remainingRealTimeFormatted = null
      this.processDescription = null
      this.tripTaktTime = undefined
      this.defaultTimer()
    }
  }

  private defaultTimer() {

    if (!this.shouldUpdateDockInfo()) {
      return;
    }
    this.currentTimeInMs = Date.now()

    const statusTime = Number(this.dockData?.status);
    const standartTakt: number = 60 * 60 * 1000;

    const remainingTime: number = standartTakt + statusTime - this.currentTimeInMs;

    this.takt = this.datesService.formatTime(remainingTime);
    this.taktBackgroundColor(remainingTime, standartTakt);
    this.activeDock = true;
  }

  private resetTimer() {
    const loadingTimer = "--:--:--";
    this.activeDock = false;
    this.bgColor = "#FFF";
    this.takt = loadingTimer;
    this.tripDBData = undefined;
    this.tripTaktTime = undefined;
    //this.dockData!.truck = 0;
  }

  private clearDisplayedData(): void {
    this.tripDBData = undefined;
    this.dockData = undefined;
    this.definedArrival = null;
    this.definedDeparture = null;
    this.remainingRealTimeFormatted = null;
    this.tripTaktTime = undefined;
    this.takt = '--:--:--';
    this.bgColor = '#FFF';
    this.activeDock = false;
  }

  private shouldUpdateDockInfo() {
    return this.dockData?.truck == 1 && this.dockData.active == 1;
  }

  checkData() {
    this.currentTimeInMs = Date.now()

    const currentTime = this.datesService.convertTimeStringToMs(
      this.datesService.extractTimeFromTimeStamp(this.currentTimeInMs));
    const statusTime = this.datesService.convertTimeStringToMs(
      this.datesService.formatTimeToJsDate(this.dockData?.status));

    this.motoresTransmissaoService.getTripByIdView(this.tripDBData!.trip!)
      .pipe(
        take(1),
        catchError(() => of(null))
      )
      .subscribe((response: MotoresTransmissaoTimeTableModel | null) => {
        if (!response) {
          this.definedArrival = null
          this.definedDeparture = null
          this.remainingRealTimeFormatted = null
          this.tripTaktTime = undefined
          return
        }

        const isExtra = response.extra != null
        let taktTime: number;

        this.isExtraTrip = isExtra
        this.definedArrival = null
        this.processDescription = response.processDescription


        if (isExtra) {
          this.definedArrival = null
          this.definedDeparture = null
          taktTime = this.datesService.convertTimeStringToMs(response.extra!);
        }
        else {
          this.definedArrival = response.arrivalTimeSLA
          this.definedDeparture = response.departureTimeSLA

          taktTime = this.datesService.convertTimeStringToMs(
            this.datesService.calculateDifference(this.definedArrival!, this.definedDeparture!))

          this.remainingRealTimeFormatted = this.datesService.calculateDeltaFormatted(this.definedDeparture!)
        }

        this.tripTaktTime = taktTime
        this.refreshTripTimer(statusTime, currentTime)
      });
  }

  private refreshTripTimer(statusTime?: number, currentTime?: number): void {
    if (this.tripTaktTime == null || !this.dockData?.status) return

    const current = currentTime ?? this.datesService.convertTimeStringToMs(
      this.datesService.extractTimeFromTimeStamp(Date.now()))
    const status = statusTime ?? this.datesService.convertTimeStringToMs(
      this.datesService.formatTimeToJsDate(this.dockData.status))
    const delta = this.datesService.calculateDelta(status, current, this.tripTaktTime)

    this.takt = formatDate(Math.abs(delta), 'HH:mm:ss', 'en-US', 'UTC')
    this.taktBackgroundColor(delta, this.tripTaktTime)

    if (this.definedDeparture) {
      this.remainingRealTimeFormatted = this.datesService.calculateDeltaFormatted(this.definedDeparture)
    }
  }

  //* -------------------------------------------- COLORAÇÃO TELA DE FUNDO  -------------------------------------------- *//

  private taktBackgroundColor(remainingTime: number, standartTakt: number) {
    if (remainingTime < 0) {
      this.bgColor = '#FF2340';
      this.takt = '-' + this.takt;
    } else if (remainingTime < (standartTakt) / 3) {
      this.bgColor = '#F1C21B';
    } else {
      this.bgColor = '#3CB371';
    }
  }

  getArrivalColor(): string {
    if (!this.dockData?.status || !this.definedArrival) return "#FFF"

    const realDate = this.datesService
      .formatTimeToJsDate(new Date(this.dockData.status).toString())
    const plannedDate = this.datesService
      .formatTimeToJsDate(this.datesService.convertTimeStringToValidDate(this.definedArrival)!.toString())

    if (plannedDate) {
      return realDate > plannedDate ? "#FF2340" : "#3CB371";
    } return "#FFF"
  }

  getDepartureColor(): string {
    if (!this.dockData?.status || !this.definedDeparture) return "#FFF"

    const now = new Date();
    const plannedDeparture = this.datesService.convertTimeStringToValidDate(this.definedDeparture!);

    const remaining = plannedDeparture!.getTime() - now.getTime()
    return remaining < 0 ? "#FF2340" : "#3CB371";
  }

  getArrivalStyle(): Record<string, string> {
    return this.getBoxStyle(this.getArrivalColor())
  }

  getDepartureStyle(): Record<string, string> {
    return this.getBoxStyle(this.getDepartureColor())
  }

  private getBoxStyle(backgroundColor: string): Record<string, string> {
    return {
      'background-color': backgroundColor,
      color: backgroundColor === '#FFF' ? '#000' : '#FFF'
    }
  }

  //* -------------------------------------------- TOGGLE -------------------------------------------- *//

  visibilityClick() {
    this.showIntegrationForm = true
  }

  toggleIntegrationForm() {
    this.showIntegrationForm = !this.showIntegrationForm
  }

  ngOnDestroy(): void {
    clearInterval(this.dateInterval);
    clearInterval(this.updateInterval);
  }

}

